import { useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { useCreateDesignOptionMutation, useGetAllBrandsQuery, useGetAllUserQuery } from '@/api/commonApis';
import CommonImage from '@/components/CommonImage';
import CustomDropdown from '@/components/CustomDropdown';
import ModalContainer from '@/components/modals/ModalContainer';
import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastSuccess } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';

const CreateDesignOption = ({ onClose, onSuccess, designDetails }) => {
	const { id } = useParams();
	const location = useLocation();
	const dispatch = useDispatch();
	const submittingRef = useRef(false);

	const [selectedValues, setSelectedValues] = useState({
		brand: null,
		user: null
	});
	const { data: brandsList } = useGetAllBrandsQuery({ params: { page: 0, size: 1000 } });
	const { data: userList } = useGetAllUserQuery({ params: { page: 0, size: 1000 } });
	const [createDesignOption, { isLoading }] = useCreateDesignOptionMutation();

	const handleValueChange = (value, name) => {
		setSelectedValues((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleSubmit = async () => {
		// Extra guard: prevents rare double-submit races (e.g. fast double-click / enter+click)
		if (submittingRef.current || isLoading) return;
		submittingRef.current = true;

		dispatch(openLoader());
		const body = {
			productIdList: [designDetails?.id],
			brandId: selectedValues?.brand?.id,
			customizerId: selectedValues?.user?.id
		};
		if (location.pathname.split('/')[1] === 'collections') {
			body.collectionId = id;
		} else if (location.pathname.split('/')[1] === 'favourite') {
			body.sourceFavoriteFolderId = id;
		}
		try {
			await createDesignOption({
				body,
				productId: designDetails?.id
			}).unwrap();

			toastSuccess('Design option created successfully');
			onSuccess?.(selectedValues);
			onClose?.();
			setSelectedValues({
				brand: null,
				user: null
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log('error==>', error);
			toastError(error?.data?.message || 'Something went wrong!');
		} finally {
			submittingRef.current = false;
			dispatch(closeLoader());
		}
	};

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="create-design-option-modal">
				<div className="popup-wrapper">
					<div className="common-popup create-design-option-modal shadow-16dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">Create design option</h3>
							</div>
							<div className="close-btn" onClick={onClose}>
								<CommonImage alt="close" src=" /icons/close.svg" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="row">
								<div className="col-md-12 pr-2">
									<div className="form-group">
										<label htmlFor="brand">Brand</label>
										<CustomDropdown
											selectedItem={selectedValues?.brand?.value}
											items={brandsList?.map((brand) => ({
												...brand,
												value: brand?.name
											}))}
											onItemClick={(value) => handleValueChange(value, 'brand')}
											title="Select Brand"
											isAddNew={false}
										/>
									</div>
								</div>
								<div className="col-md-12 pr-2">
									<div className="form-group">
										<label htmlFor="collection">
											Which designer will customize this option?
										</label>
										<CustomDropdown
											selectedItem={selectedValues?.user?.value}
											items={userList?.map((user) => ({
												...user,
												value: user?.name
											}))}
											onItemClick={(value) => handleValueChange(value, 'user')}
											title="Select user"
											isAddNew={false}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button outline size32 ml-2" onClick={onClose}>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									onClick={handleSubmit}
									disabled={
										isLoading || !selectedValues?.brand?.id || !selectedValues?.user?.id
									}
								>
									Submit
								</button>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default CreateDesignOption;
